import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { getUserAvatarUrl, handleUserAvatarError } from '../utils/avatars';
import { localeFor } from '../utils/timeFormat';
import { downloadFile, readBlobError } from '../utils/fileDownload';
import './RMCAdminPanel.css';

const RMCAdminPanel = () => {
  const { t, language } = useLanguage();

  const [activeTab, setActiveTab] = useState('requests');

  // Join requests state
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [requestAction, setRequestAction] = useState({});

  // Members state
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');

  // Invitations state
  const [invitations, setInvitations] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invEmail, setInvEmail] = useState('');
  const [invText, setInvText] = useState('');
  const [invSending, setInvSending] = useState(false);
  const [invSuccess, setInvSuccess] = useState('');
  const [invError, setInvError] = useState('');

  // Approve modal state
  const [approveModal, setApproveModal] = useState(null); // { id, userName, userEmail }
  const [approveMessage, setApproveMessage] = useState('');
  const [approving, setApproving] = useState(false);

  // Reject modal state
  const [rejectModal, setRejectModal] = useState(null); // { id, userName, userEmail }
  const [rejectReason, setRejectReason] = useState('');
  const [rejectReasonError, setRejectReasonError] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  // Message member modal state
  const [messageModal, setMessageModal] = useState(null); // { id, userName, userEmail }
  const [messageSubject, setMessageSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [messageBodyError, setMessageBodyError] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageError, setMessageError] = useState('');

  // Revoke access modal state
  const [revokeModal, setRevokeModal] = useState(null); // { id, userName, userEmail }
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState('');

  // Export state (XLSX и PDF делят строку ошибки: она одна на всю панель)
  const [exporting, setExporting] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  // Verification ride state
  const [verificationUrl, setVerificationUrl] = useState('');
  const [verificationSending, setVerificationSending] = useState({});
  const [verificationSendingAll, setVerificationSendingAll] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState('');
  const [verificationError, setVerificationError] = useState('');

  useEffect(() => {
    loadRequests();
    loadMembers();
    loadInvitations();
  }, []);

  const loadRequests = async () => {
    setRequestsLoading(true);
    try {
      const res = await axios.get('/api/rmc/join_requests');
      setRequests(res.data);
    } catch (err) {
      console.error('Failed to load join requests', err);
    } finally {
      setRequestsLoading(false);
    }
  };

  const loadMembers = async () => {
    setMembersLoading(true);
    try {
      const res = await axios.get('/api/rmc/join_requests/members');
      setMembers(res.data);
    } catch (err) {
      console.error('Failed to load members', err);
    } finally {
      setMembersLoading(false);
    }
  };

  const loadInvitations = async () => {
    setInvLoading(true);
    try {
      const res = await axios.get('/api/rmc/invitations');
      setInvitations(res.data);
    } catch (err) {
      console.error('Failed to load invitations', err);
    } finally {
      setInvLoading(false);
    }
  };

  const openApproveModal = (req) => {
    setApproveModal({ id: req.id, userName: getUserName(req.user), userEmail: req.user.email });
    setApproveMessage('');
  };

  const closeApproveModal = () => {
    if (approving) return;
    setApproveModal(null);
    setApproveMessage('');
  };

  const handleApprove = async () => {
    if (!approveModal) return;
    setApproving(true);
    try {
      await axios.post(`/api/rmc/join_requests/${approveModal.id}/approve`, {
        message: approveMessage.trim() || undefined
      });
      setRequests(prev => prev.filter(r => r.id !== approveModal.id));
      setApproveModal(null);
      setApproveMessage('');
    } catch (err) {
      console.error('Failed to approve', err);
    } finally {
      setApproving(false);
    }
  };

  const openRejectModal = (req) => {
    setRejectModal({ id: req.id, userName: getUserName(req.user), userEmail: req.user.email });
    setRejectReason('');
    setRejectReasonError(false);
  };

  const closeRejectModal = () => {
    if (rejecting) return;
    setRejectModal(null);
    setRejectReason('');
    setRejectReasonError(false);
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    if (!rejectReason.trim()) {
      setRejectReasonError(true);
      return;
    }
    setRejecting(true);
    try {
      await axios.post(`/api/rmc/join_requests/${rejectModal.id}/reject`, {
        reason: rejectReason.trim()
      });
      setRequests(prev => prev.filter(r => r.id !== rejectModal.id));
      setRejectModal(null);
      setRejectReason('');
    } catch (err) {
      console.error('Failed to reject', err);
    } finally {
      setRejecting(false);
    }
  };

  const openMessageModal = (member) => {
    setMessageModal({ id: member.id, userName: getUserName(member.user), userEmail: member.user.email });
    setMessageSubject('');
    setMessageBody('');
    setMessageBodyError(false);
    setMessageError('');
  };

  const closeMessageModal = () => {
    if (sendingMessage) return;
    setMessageModal(null);
    setMessageSubject('');
    setMessageBody('');
    setMessageBodyError(false);
    setMessageError('');
  };

  const handleSendMemberMessage = async () => {
    if (!messageModal) return;
    if (!messageBody.trim()) {
      setMessageBodyError(true);
      return;
    }
    setSendingMessage(true);
    setMessageError('');
    try {
      await axios.post(`/api/rmc/join_requests/${messageModal.id}/message`, {
        subject: messageSubject.trim() || undefined,
        message: messageBody.trim()
      });
      setMessageModal(null);
      setMessageSubject('');
      setMessageBody('');
    } catch (err) {
      console.error('Failed to send message', err);
      setMessageError(err.response?.data?.error || t('rmc.admin.messageError'));
    } finally {
      setSendingMessage(false);
    }
  };

  const openRevokeModal = (member) => {
    setRevokeModal({ id: member.id, userName: getUserName(member.user), userEmail: member.user.email });
    setRevokeReason('');
    setRevokeError('');
  };

  const closeRevokeModal = () => {
    if (revoking) return;
    setRevokeModal(null);
    setRevokeReason('');
    setRevokeError('');
  };

  const handleRevoke = async () => {
    if (!revokeModal) return;
    setRevoking(true);
    setRevokeError('');
    try {
      await axios.post(`/api/rmc/join_requests/${revokeModal.id}/revoke`, {
        reason: revokeReason.trim() || undefined
      });
      setMembers(prev => prev.filter(m => m.id !== revokeModal.id));
      setRevokeModal(null);
      setRevokeReason('');
    } catch (err) {
      console.error('Failed to revoke access', err);
      setRevokeError(err.response?.data?.error || t('rmc.admin.revokeError'));
    } finally {
      setRevoking(false);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setInvSending(true);
    setInvSuccess('');
    setInvError('');
    try {
      await axios.post('/api/rmc/invitations', {
        email: invEmail,
        custom_text: invText
      });
      setInvSuccess(`${t('rmc.admin.inviteSent')} ${invEmail}`);
      setInvEmail('');
      setInvText('');
      loadInvitations();
    } catch (err) {
      setInvError(err.response?.data?.error || t('rmc.admin.inviteError'));
    } finally {
      setInvSending(false);
    }
  };

  const handleSendVerificationRide = async (memberId) => {
    if (!verificationUrl.trim()) {
      setVerificationError(t('rmc.admin.verificationUrlRequired'));
      return;
    }
    if (!window.confirm(t('rmc.admin.verificationConfirmOne'))) return;
    setVerificationSending(prev => ({ ...prev, [memberId]: true }));
    setVerificationSuccess('');
    setVerificationError('');
    try {
      const res = await axios.post(`/api/rmc/join_requests/${memberId}/send_verification_ride`, {
        verification_ride_url: verificationUrl.trim()
      });
      setMembers(prev => prev.map(m =>
        m.id === memberId
          ? { ...m, verification_ride_sent_at: res.data.sent_at, verification_ride_url: verificationUrl.trim() }
          : m
      ));
      setVerificationSuccess(t('rmc.admin.verificationSent'));
    } catch (err) {
      setVerificationError(err.response?.data?.error || t('rmc.admin.verificationError'));
    } finally {
      setVerificationSending(prev => ({ ...prev, [memberId]: false }));
    }
  };

  const handleSendVerificationRideToAll = async () => {
    if (!verificationUrl.trim()) {
      setVerificationError(t('rmc.admin.verificationUrlRequired'));
      return;
    }
    if (!window.confirm(t('rmc.admin.verificationConfirmAll'))) return;
    setVerificationSendingAll(true);
    setVerificationSuccess('');
    setVerificationError('');
    try {
      await axios.post('/api/rmc/join_requests/send_verification_ride_to_all', {
        verification_ride_url: verificationUrl.trim()
      });
      setVerificationSuccess(t('rmc.admin.verificationSentToAll'));
      loadMembers();
    } catch (err) {
      setVerificationError(err.response?.data?.error || t('rmc.admin.verificationError'));
    } finally {
      setVerificationSendingAll(false);
    }
  };

  // Файлы собирает бэкенд: станок для тех, кто не указал его в заявке,
  // достается из активностей, у фронта этих данных нет
  const handleExportXlsx = async () => {
    setExporting(true);
    setExportError('');
    try {
      await downloadFile('/api/rmc/join_requests/export', { fallbackName: 'rmc-members.xlsx' });
    } catch (err) {
      console.error('Failed to export members', err);
      setExportError(t('rmc.admin.exportError'));
    } finally {
      setExporting(false);
    }
  };

  // Язык интерфейса уезжает на бэкенд: подписи в документе печатаются на нем же
  const handleExportPdf = async () => {
    setPdfExporting(true);
    setExportError('');
    try {
      await downloadFile('/api/rmc/join_requests/export_pdf', {
        params: { language },
        fallbackName: 'rmc-roster.pdf'
      });
    } catch (err) {
      console.error('Failed to export roster PDF', err);
      const details = await readBlobError(err);
      setExportError([t('rmc.admin.exportPdfError'), details].filter(Boolean).join(': '));
    } finally {
      setPdfExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(localeFor(language), {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getUserName = (user) => {
    return language !== 'ru' && user.first_name_eng && user.last_name_eng
      ? `${user.first_name_eng} ${user.last_name_eng}`
      : `${user.first_name} ${user.last_name}`;
  };

  const memberSearchQuery = memberSearch.trim().toLowerCase();
  const filteredMembers = memberSearchQuery
    ? members.filter(m => {
        const haystack = [
          m.user.first_name,
          m.user.last_name,
          m.user.first_name_eng,
          m.user.last_name_eng,
          m.user.nickname,
          m.user.email,
          m.trainer,
          m.power_category
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(memberSearchQuery);
      })
    : members;

  return (
    <div className="rmc-admin-page">
      <div className="rmc-admin-header">
        <h1>⚙️ {t('rmc.admin.title')}</h1>
      </div>

      <div className="rmc-admin-tabs">
        <button
          className={`rmc-tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
          onClick={() => setActiveTab('requests')}
        >
          {t('rmc.admin.joinRequestsTab')}
          {requests.length > 0 && (
            <span className="rmc-tab-badge">{requests.length}</span>
          )}
        </button>
        <button
          className={`rmc-tab-btn ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          {t('rmc.admin.membersTab')}
          {members.length > 0 && (
            <span className="rmc-tab-badge rmc-tab-badge-members">{members.length}</span>
          )}
        </button>
        <button
          className={`rmc-tab-btn ${activeTab === 'invitations' ? 'active' : ''}`}
          onClick={() => setActiveTab('invitations')}
        >
          {t('rmc.admin.invitationsTab')}
        </button>
      </div>

      {activeTab === 'requests' && (
        <div className="rmc-admin-section">
          {requestsLoading ? (
            <div className="rmc-admin-loading">{t('common.loading')}</div>
          ) : requests.length === 0 ? (
            <div className="rmc-admin-empty">✅ {t('rmc.admin.noJoinRequests')}</div>
          ) : (
            <div className="rmc-request-list">
              {requests.map(req => (
                <div key={req.id} className="rmc-request-card">
                  <div className="rmc-request-user">
                    <img
                      src={getUserAvatarUrl(req.user.avatar)}
                      alt=""
                      className="rmc-request-avatar"
                      onError={handleUserAvatarError}
                    />
                    <div className="rmc-request-user-info">
                      <strong>{getUserName(req.user)}</strong>
                      <span className="rmc-request-nickname">@{req.user.nickname}</span>
                      {req.user.email && (
                        <span className="rmc-request-email">{req.user.email}</span>
                      )}
                    </div>
                    <span className={`rmc-request-source rmc-source-${req.source}`}>
                      {t(`rmc.admin.source_${req.source}`)}
                    </span>
                    <span className="rmc-request-date">{formatDate(req.created_at)}</span>
                  </div>
                  <div className="rmc-request-details">
                    {req.trainer && (
                      <span className="rmc-request-detail">
                        <strong>{t('rmc.admin.trainer')}:</strong> {req.trainer}
                      </span>
                    )}
                    {req.power_category && (
                      <span className="rmc-request-detail">
                        <strong>{t('rmc.admin.powerCategory')}:</strong> {req.power_category}
                      </span>
                    )}
                  </div>
                  {req.message && (
                    <div className="rmc-request-message">
                      <span className="rmc-request-message-label">{t('rmc.admin.requestMessage')}</span>
                      <p>{req.message}</p>
                    </div>
                  )}
                  <div className="rmc-request-actions">
                    <button
                      className="btn btn-success rmc-action-btn"
                      onClick={() => openApproveModal(req)}
                      disabled={!!requestAction[req.id]}
                    >
                      {t('rmc.admin.approve')}
                    </button>
                    <button
                      className="btn btn-danger rmc-action-btn"
                      onClick={() => openRejectModal(req)}
                      disabled={!!requestAction[req.id]}
                    >
                      {t('rmc.admin.reject')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'members' && (
        <div className="rmc-admin-section">
          <div className="rmc-verification-form-card">
            <h3>{t('rmc.admin.verificationRide')}</h3>
            <div className="rmc-verification-form">
              <div className="rmc-verification-input-row">
                <input
                  type="url"
                  value={verificationUrl}
                  onChange={e => { setVerificationUrl(e.target.value); setVerificationError(''); }}
                  placeholder={t('rmc.admin.verificationRideUrlPlaceholder')}
                  className="rmc-verification-url-input"
                />
                <button
                  className="rmc-send-all-btn"
                  onClick={handleSendVerificationRideToAll}
                  disabled={verificationSendingAll || members.length === 0}
                >
                  {verificationSendingAll ? t('rmc.admin.sendingVerification') : t('rmc.admin.sendToAll')}
                </button>
              </div>
              {verificationSuccess && <div className="success-message">{verificationSuccess}</div>}
              {verificationError && <div className="error-message">{verificationError}</div>}
            </div>
          </div>

          {members.length > 0 && (
            <>
              <div className="rmc-member-search-row">
                <div className="rmc-member-search-field">
                  <input
                    type="text"
                    value={memberSearch}
                    onChange={e => setMemberSearch(e.target.value)}
                    placeholder={t('rmc.admin.memberSearchPlaceholder')}
                    className="rmc-member-search-input"
                  />
                  {memberSearch && (
                    <button
                      type="button"
                      className="rmc-member-search-clear"
                      onClick={() => setMemberSearch('')}
                      aria-label={t('rmc.admin.memberSearchClear')}
                    >
                      &times;
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className="rmc-member-export-btn"
                  onClick={handleExportXlsx}
                  disabled={exporting}
                  title={t('rmc.admin.exportXlsxHint')}
                >
                  {exporting ? t('rmc.admin.exportingXlsx') : t('rmc.admin.exportXlsx')}
                </button>
                <button
                  type="button"
                  className="rmc-member-export-btn rmc-member-pdf-btn"
                  onClick={handleExportPdf}
                  disabled={pdfExporting}
                  title={t('rmc.admin.exportPdfHint')}
                >
                  {pdfExporting ? t('rmc.admin.exportingPdf') : t('rmc.admin.exportPdf')}
                </button>
              </div>
              {exportError && <div className="error-message">{exportError}</div>}
            </>
          )}

          {membersLoading ? (
            <div className="rmc-admin-loading">{t('common.loading')}</div>
          ) : members.length === 0 ? (
            <div className="rmc-admin-empty">{t('rmc.admin.noMembers')}</div>
          ) : filteredMembers.length === 0 ? (
            <div className="rmc-admin-empty">{t('rmc.admin.noMemberSearchResults')}</div>
          ) : (
            <div className="rmc-members-list">
              <div className="rmc-members-header">
                <span className="rmc-mh-num">#</span>
                <span className="rmc-mh-name">{t('rmc.admin.memberName')}</span>
                <span className="rmc-mh-trainer">{t('rmc.admin.trainer')}</span>
                <span className="rmc-mh-category">{t('rmc.admin.powerCategory')}</span>
                <span className="rmc-mh-source">{t('rmc.admin.memberSource')}</span>
                <span className="rmc-mh-rules">{t('rmc.admin.memberRules')}</span>
                <span className="rmc-mh-date">{t('rmc.admin.memberDate')}</span>
                <span className="rmc-mh-verification">{t('rmc.admin.verificationRide')}</span>
                <span className="rmc-mh-actions">{t('rmc.admin.memberActions')}</span>
              </div>
              {filteredMembers.map((m, idx) => (
                <div key={m.id} className="rmc-member-row">
                  <span className="rmc-mc-num">{idx + 1}</span>
                  <div className="rmc-mc-name">
                    <img
                      src={getUserAvatarUrl(m.user.avatar)}
                      alt=""
                      className="rmc-member-avatar"
                      onError={handleUserAvatarError}
                    />
                    <div>
                      <strong>{getUserName(m.user)}</strong>
                      <span className="rmc-member-nickname">@{m.user.nickname}</span>
                    </div>
                  </div>
                  <span className="rmc-mc-trainer" data-label={t('rmc.admin.trainer')}>{m.trainer || '—'}</span>
                  <span className="rmc-mc-category" data-label={t('rmc.admin.powerCategory')}>{m.power_category || '—'}</span>
                  <span className={`rmc-mc-source rmc-request-source rmc-source-${m.source}`} data-label={t('rmc.admin.memberSource')}>
                    {t(`rmc.admin.source_${m.source}`)}
                  </span>
                  <span className={`rmc-mc-rules ${m.rules_accepted ? 'accepted' : 'pending'}`} data-label={t('rmc.admin.memberRules')}>
                    {m.rules_accepted ? '✅' : '⏳'}
                  </span>
                  <span className="rmc-mc-date" data-label={t('rmc.admin.memberDate')}>{formatDate(m.approved_at)}</span>
                  <span className="rmc-mc-verification" data-label={t('rmc.admin.verificationRide')}>
                    {m.verification_ride_sent_at ? (
                      <span className="rmc-verification-status sent" title={formatDate(m.verification_ride_sent_at)}>
                        ✅ {formatDate(m.verification_ride_sent_at)}
                      </span>
                    ) : (
                      <button
                        className="rmc-send-verification-btn"
                        onClick={() => handleSendVerificationRide(m.id)}
                        disabled={verificationSending[m.id] || !verificationUrl.trim()}
                        title={!verificationUrl.trim() ? t('rmc.admin.verificationUrlRequired') : t('rmc.admin.sendVerificationRide')}
                      >
                        {verificationSending[m.id] ? '...' : t('rmc.admin.sendVerificationRide')}
                      </button>
                    )}
                  </span>
                  <span className="rmc-mc-actions" data-label={t('rmc.admin.memberActions')}>
                    <button
                      className="rmc-member-action-btn rmc-message-btn"
                      onClick={() => openMessageModal(m)}
                      title={t('rmc.admin.messageMember')}
                      aria-label={t('rmc.admin.messageMember')}
                    >
                      <span className="rmc-action-icon">✉️</span>
                      <span className="rmc-action-text">{t('rmc.admin.messageMember')}</span>
                    </button>
                    <button
                      className="rmc-member-action-btn rmc-revoke-btn"
                      onClick={() => openRevokeModal(m)}
                      title={t('rmc.admin.revokeMember')}
                      aria-label={t('rmc.admin.revokeMember')}
                    >
                      <span className="rmc-action-icon">🚫</span>
                      <span className="rmc-action-text">{t('rmc.admin.revokeMember')}</span>
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {approveModal && (
        <div className="rmc-approve-modal-overlay" onClick={closeApproveModal}>
          <div className="rmc-approve-modal" onClick={e => e.stopPropagation()}>
            <div className="rmc-approve-modal-header">
              <h3>{t('rmc.admin.approve')}</h3>
              <button className="rmc-approve-modal-close" onClick={closeApproveModal} disabled={approving}>&times;</button>
            </div>
            <div className="rmc-approve-modal-body">
              <p className="rmc-approve-modal-desc">
                {t('rmc.admin.approveModalDesc').replace('{name}', approveModal.userName)}
              </p>
              {approveModal.userEmail && (
                <p className="rmc-approve-modal-email">
                  {t('rmc.admin.approveModalEmailTo')}: <strong>{approveModal.userEmail}</strong>
                </p>
              )}
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label>{t('rmc.admin.approveModalMessageLabel')}</label>
                <textarea
                  value={approveMessage}
                  onChange={e => setApproveMessage(e.target.value)}
                  placeholder={t('rmc.admin.approveModalMessagePlaceholder')}
                  rows={4}
                  className="rmc-textarea"
                  disabled={approving}
                />
              </div>
            </div>
            <div className="rmc-approve-modal-footer">
              <button className="btn btn-secondary" onClick={closeApproveModal} disabled={approving}>
                {t('rmc.admin.approveModalCancel')}
              </button>
              <button className="btn btn-success" onClick={handleApprove} disabled={approving}>
                {approving ? '...' : t('rmc.admin.approveModalConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectModal && (
        <div className="rmc-approve-modal-overlay" onClick={closeRejectModal}>
          <div className="rmc-approve-modal" onClick={e => e.stopPropagation()}>
            <div className="rmc-approve-modal-header">
              <h3>{t('rmc.admin.reject')}</h3>
              <button className="rmc-approve-modal-close" onClick={closeRejectModal} disabled={rejecting}>&times;</button>
            </div>
            <div className="rmc-approve-modal-body">
              <p className="rmc-approve-modal-desc">
                {t('rmc.admin.rejectModalDesc').replace('{name}', rejectModal.userName)}
              </p>
              {rejectModal.userEmail && (
                <p className="rmc-approve-modal-email">
                  {t('rmc.admin.approveModalEmailTo')}: <strong>{rejectModal.userEmail}</strong>
                </p>
              )}
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label>
                  {t('rmc.admin.rejectModalReasonLabel')}
                  <span style={{ color: 'var(--c-e74c3c-fg)' }}> *</span>
                </label>
                <textarea
                  value={rejectReason}
                  onChange={e => { setRejectReason(e.target.value); setRejectReasonError(false); }}
                  placeholder={t('rmc.admin.rejectModalReasonPlaceholder')}
                  rows={4}
                  className={`rmc-textarea${rejectReasonError ? ' rmc-textarea-error' : ''}`}
                  disabled={rejecting}
                />
                {rejectReasonError && (
                  <span className="rmc-field-error">{t('rmc.admin.rejectModalReasonRequired')}</span>
                )}
              </div>
            </div>
            <div className="rmc-approve-modal-footer">
              <button className="btn btn-secondary" onClick={closeRejectModal} disabled={rejecting}>
                {t('rmc.admin.approveModalCancel')}
              </button>
              <button className="btn btn-danger" onClick={handleReject} disabled={rejecting}>
                {rejecting ? '...' : t('rmc.admin.rejectModalConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {messageModal && (
        <div className="rmc-approve-modal-overlay" onClick={closeMessageModal}>
          <div className="rmc-approve-modal" onClick={e => e.stopPropagation()}>
            <div className="rmc-approve-modal-header">
              <h3>{t('rmc.admin.messageMember')}</h3>
              <button className="rmc-approve-modal-close" onClick={closeMessageModal} disabled={sendingMessage}>&times;</button>
            </div>
            <div className="rmc-approve-modal-body">
              <p className="rmc-approve-modal-desc">
                {t('rmc.admin.messageModalDesc').replace('{name}', messageModal.userName)}
              </p>
              {messageModal.userEmail && (
                <p className="rmc-approve-modal-email">
                  {t('rmc.admin.approveModalEmailTo')}: <strong>{messageModal.userEmail}</strong>
                </p>
              )}
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label>{t('rmc.admin.messageSubjectLabel')}</label>
                <input
                  type="text"
                  value={messageSubject}
                  onChange={e => setMessageSubject(e.target.value)}
                  placeholder={t('rmc.admin.messageSubjectPlaceholder')}
                  className="rmc-verification-url-input"
                  disabled={sendingMessage}
                />
              </div>
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label>
                  {t('rmc.admin.messageBodyLabel')}
                  <span style={{ color: 'var(--c-e74c3c-fg)' }}> *</span>
                </label>
                <textarea
                  value={messageBody}
                  onChange={e => { setMessageBody(e.target.value); setMessageBodyError(false); }}
                  placeholder={t('rmc.admin.messageBodyPlaceholder')}
                  rows={5}
                  className={`rmc-textarea${messageBodyError ? ' rmc-textarea-error' : ''}`}
                  disabled={sendingMessage}
                />
                {messageBodyError && (
                  <span className="rmc-field-error">{t('rmc.admin.messageBodyRequired')}</span>
                )}
              </div>
              {messageError && <div className="error-message">{messageError}</div>}
            </div>
            <div className="rmc-approve-modal-footer">
              <button className="btn btn-secondary" onClick={closeMessageModal} disabled={sendingMessage}>
                {t('rmc.admin.approveModalCancel')}
              </button>
              <button className="btn btn-primary" onClick={handleSendMemberMessage} disabled={sendingMessage}>
                {sendingMessage ? t('rmc.admin.sending') : t('rmc.admin.send')}
              </button>
            </div>
          </div>
        </div>
      )}

      {revokeModal && (
        <div className="rmc-approve-modal-overlay" onClick={closeRevokeModal}>
          <div className="rmc-approve-modal" onClick={e => e.stopPropagation()}>
            <div className="rmc-approve-modal-header">
              <h3>{t('rmc.admin.revokeMember')}</h3>
              <button className="rmc-approve-modal-close" onClick={closeRevokeModal} disabled={revoking}>&times;</button>
            </div>
            <div className="rmc-approve-modal-body">
              <p className="rmc-approve-modal-desc">
                {t('rmc.admin.revokeModalDesc').replace('{name}', revokeModal.userName)}
              </p>
              {revokeModal.userEmail && (
                <p className="rmc-approve-modal-email">
                  {t('rmc.admin.approveModalEmailTo')}: <strong>{revokeModal.userEmail}</strong>
                </p>
              )}
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label>{t('rmc.admin.revokeReasonLabel')}</label>
                <textarea
                  value={revokeReason}
                  onChange={e => setRevokeReason(e.target.value)}
                  placeholder={t('rmc.admin.revokeReasonPlaceholder')}
                  rows={4}
                  className="rmc-textarea"
                  disabled={revoking}
                />
              </div>
              {revokeError && <div className="error-message">{revokeError}</div>}
            </div>
            <div className="rmc-approve-modal-footer">
              <button className="btn btn-secondary" onClick={closeRevokeModal} disabled={revoking}>
                {t('rmc.admin.approveModalCancel')}
              </button>
              <button className="btn btn-danger" onClick={handleRevoke} disabled={revoking}>
                {revoking ? '...' : t('rmc.admin.revokeModalConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'invitations' && (
        <div className="rmc-admin-section">
          <div className="rmc-invite-form-card">
            <h3>{t('rmc.admin.sendInvite')}</h3>
            <form onSubmit={handleSendInvite}>
              <div className="form-group">
                <label>{t('rmc.admin.emailLabel')}</label>
                <input
                  type="email"
                  value={invEmail}
                  onChange={e => setInvEmail(e.target.value)}
                  placeholder={t('rmc.admin.emailPlaceholder')}
                  required
                />
              </div>
              <div className="form-group">
                <label>{t('rmc.admin.customTextLabel')}</label>
                <textarea
                  value={invText}
                  onChange={e => setInvText(e.target.value)}
                  placeholder={t('rmc.admin.customTextPlaceholder')}
                  rows={5}
                  className="rmc-textarea"
                />
              </div>
              {invSuccess && <div className="success-message">{invSuccess}</div>}
              {invError && <div className="error-message">{invError}</div>}
              <button type="submit" className="btn btn-primary" disabled={invSending}>
                {invSending ? t('rmc.admin.sending') : t('rmc.admin.send')}
              </button>
            </form>
          </div>

          <div className="rmc-inv-history">
            <h3>{t('rmc.admin.invitationsTab')}</h3>
            {invLoading ? (
              <div className="rmc-admin-loading">{t('common.loading')}</div>
            ) : invitations.length === 0 ? (
              <div className="rmc-admin-empty">{t('rmc.admin.noInvitations')}</div>
            ) : (
              <div className="rmc-inv-list">
                {invitations.map(inv => (
                  <div key={inv.id} className={`rmc-inv-item ${inv.used ? 'used' : 'pending'}`}>
                    <div className="rmc-inv-email">{inv.email}</div>
                    <div className="rmc-inv-meta">
                      <span className={`rmc-inv-status ${inv.used ? 'used' : 'pending'}`}>
                        {inv.used ? t('rmc.admin.invitationUsed') : t('rmc.admin.invitationPending')}
                      </span>
                      {inv.used_by && (
                        <span className="rmc-inv-used-by">
                          {t('rmc.admin.usedBy')} @{inv.used_by.nickname}
                        </span>
                      )}
                      <span className="rmc-inv-date">{formatDate(inv.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RMCAdminPanel;
